/**
 * edj-policy-enhance.js
 * Transforme le DOM natif Shopify .shopify-policy__container en layout
 * maquette EDJ (page-legal.jsx) :
 *  - Wrap dans .edj-legal avec hero (eyebrow JP + H1 serif + last-update)
 *  - Sidebar sticky (sommaire) + body
 *  - Promotion des paragraphes "ARTICLE N – TITRE" en h2
 *  - Sommaire mobile select dropdown
 *
 * Source de vérité : design-system/edj/project/page-legal.jsx (LegalShell)
 * Sécurité : aucun innerHTML — uniquement DOM API.
 */
(function () {
  'use strict';

  function init() {
    const container = document.querySelector('.shopify-policy__container');
    if (!container) return;

    const titleEl = container.querySelector('.shopify-policy__title h1');
    const bodyEl = container.querySelector('.shopify-policy__body .rte') || container.querySelector('.shopify-policy__body');
    if (!titleEl || !bodyEl) return;

    const titleText = titleEl.textContent.trim();

    // ─── Build new wrapper structure ────────────────────────────────
    const wrapper = document.createElement('div');
    wrapper.className = 'edj-legal edj-policy';

    // Hero
    const hero = document.createElement('header');
    hero.className = 'edj-legal__hero';
    const eyebrow = document.createElement('div');
    eyebrow.className = 'edj-legal__hero-eyebrow';
    eyebrow.appendChild(document.createTextNode('Pages légales '));
    const jp = document.createElement('span');
    jp.lang = 'ja';
    jp.textContent = '· 法的事項';
    eyebrow.appendChild(jp);
    hero.appendChild(eyebrow);
    const h1 = document.createElement('h1');
    h1.className = 'edj-legal__hero-title';
    h1.textContent = titleText;
    hero.appendChild(h1);
    const update = document.createElement('p');
    update.className = 'edj-legal__hero-update';
    update.appendChild(document.createTextNode('Dernière mise à jour : '));
    const em = document.createElement('em');
    em.textContent = (new Date()).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
    update.appendChild(em);
    update.appendChild(document.createTextNode(' · Version applicable.'));
    hero.appendChild(update);
    wrapper.appendChild(hero);

    // ─── Process body : promote ARTICLE N – TITLE ───────────────────
    // Robustesse au paste : selon l'éditeur Shopify (Pages, Policies, Articles)
    // le copier-coller depuis du texte produit parfois `.rte > <p>` (idéal),
    // parfois `.rte > <div> > <div><span>...</span></div><br>...` (cas Policies).
    // On normalise : on descend dans tout wrapper unique sans classe propre,
    // puis on prend les éléments-blocs (p/div/h2/h3/li) qui contiennent du texte
    // direct (pas un autre bloc imbriqué).
    let workingEl = bodyEl;
    while (workingEl.children.length === 1
           && workingEl.firstElementChild.tagName === 'DIV'
           && !workingEl.firstElementChild.className) {
      workingEl = workingEl.firstElementChild;
    }
    const BLOCK_TAGS = new Set(['P', 'DIV', 'H1', 'H2', 'H3', 'H4', 'H5', 'LI']);
    const candidates = Array.from(workingEl.children).filter((el) => {
      if (!BLOCK_TAGS.has(el.tagName)) return false;
      // Ne prendre que les blocs feuilles (texte direct, pas d'autre bloc imbriqué).
      return !el.querySelector('p, div, h1, h2, h3, h4, h5');
    });

    const sections = [];
    let counter = 0;
    candidates.forEach((el) => {
      const text = (el.textContent || '').trim();
      if (!text) return;
      const articleMatch = text.match(/^ARTICLE\s+(\d+)\s*[–\-—]\s*(.+)$/i);
      // Auto-promotion d'un paragraphe en MAJUSCULES (4-80 chars) — élargi au DIV
      // pour gérer le cas paste-from-markdown qui rend chaque ligne en <div>.
      const upperShort = !articleMatch
        && (el.tagName === 'P' || el.tagName === 'DIV')
        && /^[A-ZÀ-Ü\s'’\-–—,.()0-9]{4,80}$/u.test(text);
      if (articleMatch || upperShort || ['H2', 'H3'].includes(el.tagName)) {
        counter += 1;
        const id = 'edj-section-' + counter;
        const title = articleMatch ? articleMatch[2].trim() : text;
        // Numérotation visuelle TOC = compteur séquentiel (01, 02, 03…)
        // toujours, indépendamment du numéro éventuel dans "ARTICLE N – TITRE".
        // Évite les doublons quand un policy mélange un chapeau MAJ + des ARTICLE
        // (ex: CGV native qui commence par "VUE D'ENSEMBLE" + 21 ARTICLE).
        const num = String(counter).padStart(2, '0');

        const h2 = document.createElement('h2');
        h2.id = id;
        h2.className = 'edj-policy__h2';
        const numSpan = document.createElement('span');
        numSpan.className = 'edj-policy__h2-num';
        numSpan.textContent = num;
        h2.appendChild(numSpan);
        h2.appendChild(document.createTextNode(title));
        el.parentNode.replaceChild(h2, el);
        sections.push({ id, title, num });
      }
    });

    // ─── Body grid (with or without sidebar) ────────────────────────
    const bodyWrap = document.createElement('div');
    bodyWrap.className = sections.length >= 2
      ? 'edj-legal__body edj-legal__body--with-sidebar'
      : 'edj-legal__body';

    if (sections.length >= 2) {
      const aside = document.createElement('aside');
      aside.className = 'edj-legal__sidebar';
      aside.setAttribute('aria-label', 'Sommaire');
      const label = document.createElement('div');
      label.className = 'edj-legal__sidebar-label';
      label.textContent = 'Sommaire';
      aside.appendChild(label);
      const nav = document.createElement('nav');
      const ol = document.createElement('ol');
      ol.className = 'edj-legal__toc';
      sections.forEach((s) => {
        const li = document.createElement('li');
        const a = document.createElement('a');
        a.className = 'edj-legal__toc-link';
        a.href = '#' + s.id;
        const numSp = document.createElement('span');
        numSp.className = 'edj-legal__toc-num';
        numSp.textContent = String(s.num).padStart(2, '0') + '.';
        a.appendChild(numSp);
        a.appendChild(document.createTextNode(' ' + s.title));
        a.dataset.target = s.id;
        a.addEventListener('click', (ev) => {
          ev.preventDefault();
          const t = document.getElementById(s.id);
          if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        li.appendChild(a);
        ol.appendChild(li);
      });
      nav.appendChild(ol);
      aside.appendChild(nav);

      const help = document.createElement('div');
      help.className = 'edj-legal__sidebar-help';
      const helpLabel = document.createElement('div');
      helpLabel.className = 'edj-legal__sidebar-help-label';
      helpLabel.textContent = 'Une question ?';
      const helpText = document.createElement('p');
      helpText.className = 'edj-legal__sidebar-help-text';
      helpText.textContent = 'Notre service client est joignable du lundi au vendredi.';
      const helpLink = document.createElement('a');
      helpLink.className = 'edj-legal__sidebar-help-link';
      helpLink.href = 'mailto:contact@esprit-du-japon.fr';
      helpLink.textContent = 'contact@esprit-du-japon.fr';
      help.appendChild(helpLabel);
      help.appendChild(helpText);
      help.appendChild(helpLink);
      aside.appendChild(help);
      bodyWrap.appendChild(aside);
    }

    // Content
    const content = document.createElement('div');
    content.className = 'edj-legal__content';

    if (sections.length >= 2) {
      // Mobile quick-toc
      const quick = document.createElement('div');
      quick.className = 'edj-legal__quicktoc';
      const qLabel = document.createElement('div');
      qLabel.className = 'edj-legal__quicktoc-label';
      qLabel.textContent = 'Sommaire rapide';
      const select = document.createElement('select');
      select.className = 'edj-legal__quicktoc-select';
      select.setAttribute('aria-label', 'Naviguer dans le document');
      sections.forEach((s, i) => {
        const opt = document.createElement('option');
        opt.value = s.id;
        opt.textContent = (i + 1) + '. ' + s.title;
        select.appendChild(opt);
      });
      select.addEventListener('change', (ev) => {
        const t = document.getElementById(ev.target.value);
        if (t) t.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
      quick.appendChild(qLabel);
      quick.appendChild(select);
      content.appendChild(quick);
    }

    // Body content (move bodyEl children inside)
    const bodyInner = document.createElement('div');
    bodyInner.className = 'edj-legal__section edj-policy__body';
    while (bodyEl.firstChild) {
      bodyInner.appendChild(bodyEl.firstChild);
    }
    content.appendChild(bodyInner);

    // Foot
    const foot = document.createElement('div');
    foot.className = 'edj-legal__foot';
    foot.appendChild(document.createTextNode(
      'Document à valeur légale · En cas de divergence avec d’éventuelles traductions, la version française fait foi · Pour toute question : '
    ));
    const footLink = document.createElement('a');
    footLink.href = 'mailto:contact@esprit-du-japon.fr';
    footLink.textContent = 'contact@esprit-du-japon.fr';
    foot.appendChild(footLink);
    foot.appendChild(document.createTextNode('.'));
    content.appendChild(foot);

    bodyWrap.appendChild(content);
    wrapper.appendChild(bodyWrap);

    // Replace original container
    container.parentNode.replaceChild(wrapper, container);

    // Scroll-spy
    if (sections.length >= 2 && 'IntersectionObserver' in window) {
      const links = wrapper.querySelectorAll('[data-target]');
      const linkMap = {};
      links.forEach((l) => { linkMap[l.dataset.target] = l; });
      const obs = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            const link = linkMap[e.target.id];
            if (!link) return;
            Object.values(linkMap).forEach((l) => l.classList.remove('is-active'));
            link.classList.add('is-active');
          }
        });
      }, { rootMargin: '-20% 0px -60% 0px' });
      sections.forEach((s) => {
        const t = document.getElementById(s.id);
        if (t) obs.observe(t);
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
